import { useViewer } from "@/authentication/ViewerProvider"
import { Button } from "@/components/ui/button"
import ConfirmationDialog from "@/components/ui/confirmation-dialog"
import ErrorScreen from "@/components/ui/error-screen"
import { FieldGroup } from "@/components/ui/field-group"
import { FormActions } from "@/components/ui/form-actions"
import { RhfField } from "@/components/ui/rhf-field"
import { useToast } from "@/components/ui/use-toast"
import { gql } from "@/generated"
import { useCurrentOrganization } from "@/novus/hooks/useCurrentOrganization"
import { displayValidationErrors, useMinimumPayment } from "@/util/validations"
import { useQuery } from "@apollo/client"
import { zodResolver } from "@hookform/resolvers/zod"
import React, { useEffect, useMemo, useState } from "react"
import { FormProvider, SubmitHandler, useForm } from "react-hook-form"
import { z, ZodType } from "zod"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

const DeductionFiveTwoNineAccounts_Query = gql(`
  query DeductionFiveTwoNineAccounts($userId: ID) {
    fiveTwoNineAccounts(
      userId: $userId
    ) {
      nodes {
        id
        beneficiaryFirstName
        beneficiaryLastName
        accountNumber
        program {
          name
        }
      }
    }
  }
`)

export type DeductionFormValues = {
  fiveTwoNineAccountId: string
  amount: string | number
}

const maximumDeductionAmount =
  Number(window.novus.maximumContributionAmountCents) / 100

const DeductionForm = ({
  onSubmit,
  defaultValues,
  isNew,
}: {
  onSubmit: SubmitHandler<DeductionFormValues>
  defaultValues: DeductionFormValues
  isNew?: boolean
}) => {
  const { viewer } = useViewer()
  const { organization } = useCurrentOrganization()
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [showPayrollLockedInfo, setShowPayrollLockedInfo] = useState(false)
  const [fiveTwoNineAccountId, setFiveTwoNineAccountId] = useState<
    string | undefined
  >()
  const { minimum, loading: minimumLoading } =
    useMinimumPayment(fiveTwoNineAccountId)
  const schema: ZodType<DeductionFormValues> = useMemo(
    () =>
      z.object({
        fiveTwoNineAccountId: z.string().min(1, "Beneficiary is required"),
        amount: z.coerce
          .number()
          .min(minimum || 1, `Amount must be greater than $${minimum || 1}`)
          .max(
            maximumDeductionAmount,
            `Deductions over $${maximumDeductionAmount} are not supported at this time. Please adjust your amount.`,
          ),
      }),
    [minimum],
  )

  const methods = useForm<DeductionFormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  })
  const { control, formState, setError } = methods

  const formFiveTwoNineAccountId = methods.watch("fiveTwoNineAccountId")
  useEffect(() => {
    setFiveTwoNineAccountId(formFiveTwoNineAccountId)
  }, [formFiveTwoNineAccountId])

  const { data, loading, error } = useQuery(
    DeductionFiveTwoNineAccounts_Query,
    {
      variables: { userId: viewer.id },
    },
  )

  const { toast } = useToast()

  const onInternalSubmit = async (data: DeductionFormValues) => {
    try {
      await onSubmit(data)
    } catch (e: any) {
      console.error(e)
      displayValidationErrors({
        recordType: "deduction",
        errors: e,
        setError,
        toast,
        validationErrorTitle: "Deduction was not updated.",
      })
    }
  }

  if (loading && !data) return null
  if (error) return <ErrorScreen message={error?.message} />

  const fiveTwoNineAccounts = data?.fiveTwoNineAccounts.nodes.map((a) => {
    const name = [a.beneficiaryFirstName, a?.beneficiaryLastName]
      .filter(Boolean)
      .join(" ")
    return {
      label: [name, a.program?.name, a.accountNumber]
        .filter(Boolean)
        .join(" | "),
      value: a.id,
    }
  })

  const formatOrdinalDate = (day: number) => {
    const suffix = ["th", "st", "nd", "rd"]
    const v = day % 100
    return day + (suffix[(v - 20) % 10] || suffix[v] || suffix[0])
  }

  const getPayrollInfoCopy = () => {
    if (!organization?.deductionRepeatDay) return null

    const currentDate = formatOrdinalDate(organization.deductionRepeatDay)
    const nextDate = formatOrdinalDate(organization.deductionRepeatDay + 1)

    return {
      currentDate,
      nextDate,
    }
  }

  const payrollInfo = getPayrollInfoCopy()

  const handlePayrollLockedClick = () => {
    setShowPayrollLockedInfo(true)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      handlePayrollLockedClick()
    }
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onInternalSubmit)}>
        <FieldGroup>
          <RhfField
            name="fiveTwoNineAccountId"
            control={control}
            placeholder="Select beneficiary"
            label="Beneficiary Name"
            as="select"
            options={fiveTwoNineAccounts}
            disabled={organization?.payrollLocked}
          />
        </FieldGroup>
        <FieldGroup>
          <RhfField
            name="amount"
            control={control}
            placeholder="Amount"
            label="Amount"
            disabled={organization?.payrollLocked}
          />
        </FieldGroup>

        <FormActions>
          {isNew ? (
            <>
              <ConfirmationDialog
                title="Confirm Deduction"
                description="Please confirm that you would like to submit this deduction to your account."
                actionName="Confirm & Submit"
                onConfirm={() => {
                  methods.handleSubmit(onInternalSubmit)()
                  setShowConfirmation(false)
                }}
                isOpen={showConfirmation}
                setIsOpen={setShowConfirmation}
              />
              {organization?.payrollLocked ? (
                <div
                  onClick={handlePayrollLockedClick}
                  onKeyDown={handleKeyDown}
                  className="inline-block"
                  role="button"
                  tabIndex={0}
                >
                  <Button type="button" variant="default" disabled={true}>
                    {formState.isSubmitting
                      ? "Adding..."
                      : "Add Monthly Deduction"}
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="default"
                  disabled={formState.isSubmitting || minimumLoading}
                  onClick={() => setShowConfirmation(true)}
                >
                  {formState.isSubmitting
                    ? "Adding..."
                    : "Add Monthly Deduction"}
                </Button>
              )}
            </>
          ) : (
            <>
              {organization?.payrollLocked ? (
                <div
                  onClick={handlePayrollLockedClick}
                  onKeyDown={handleKeyDown}
                  className="inline-block"
                  role="button"
                  tabIndex={0}
                >
                  <Button type="button" variant="default" disabled={true}>
                    Update
                  </Button>
                </div>
              ) : (
                <Button
                  type="submit"
                  variant="default"
                  disabled={formState.isSubmitting || minimumLoading}
                >
                  Update
                </Button>
              )}
            </>
          )}
        </FormActions>

        <Dialog
          open={showPayrollLockedInfo}
          onOpenChange={setShowPayrollLockedInfo}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Deduction Enrollment Closed</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {payrollInfo && (
                <>
                  <p>
                    529 deduction enrollment for this month is closed until the{" "}
                    <strong>{payrollInfo.currentDate}</strong> of the month.
                  </p>
                  <p>
                    You are able to opt in and manage your 529 deductions from
                    the <strong>{payrollInfo.nextDate}</strong> of the month
                    through the cutoff date of next month. Please check back
                    during this time to manage your deductions.
                  </p>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </form>
    </FormProvider>
  )
}

export default DeductionForm
